import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { ChatBubble } from "@/components/ChatBubble";
import { ChatInput } from "@/components/ChatInput";
import type { ChatMessage, BotIcon } from "@/types/evolve";
import { Flame, Search, X, ChevronUp, Loader2, Bot, Brain, Zap, Skull, Dumbbell, Mic, Volume2, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VoiceModeToggle } from "@/components/VoiceModeToggle";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useSpeechSynthesis } from "@/hooks/useSpeechSynthesis";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Map bot icon values to Lucide components
const BOT_ICON_COMPONENTS: Record<BotIcon, React.ComponentType<{ className?: string }>> = {
  flame: Flame,
  bot: Bot,
  brain: Brain,
  zap: Zap,
  skull: Skull,
  dumbbell: Dumbbell,
};

interface ChatTabProps {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  isLoading: boolean;
  messagesUsed: number;
  messageLimit: number;
  isPremium: boolean;
  isInTrial?: boolean;
  trialDaysLeft?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onSearch?: (query: string) => Promise<ChatMessage[]>;
  botIcon?: BotIcon;
  userId?: string;
  hasWorkoutButton?: boolean;
  weightUnit?: 'lbs' | 'kg';
  hasAIConsent?: boolean;
  onRequestConsent?: () => void;
  consentDeniedMessage?: boolean;
  onDismissDeniedMessage?: () => void;
}

export function ChatTab({ 
  messages, 
  onSend, 
  isLoading, 
  messagesUsed, 
  messageLimit, 
  isPremium, 
  isInTrial, 
  trialDaysLeft,
  onLoadMore,
  hasMore,
  onSearch,
  botIcon = 'flame',
  userId,
  hasWorkoutButton,
  weightUnit = 'lbs',
  hasAIConsent,
  onRequestConsent,
  consentDeniedMessage,
  onDismissDeniedMessage
}: ChatTabProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLimitReached = !isPremium && messagesUsed >= messageLimit;
  
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ChatMessage[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Voice mode state
  const [voiceMode, setVoiceMode] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const lastSpokenMessageRef = useRef<string | null>(null);
  
  const BotIconComponent = BOT_ICON_COMPONENTS[botIcon] || Flame;
  
  // Hide voice mode on mobile (iOS Safari voice recognition is unreliable)
  const isMobile = useIsMobile();

  // Speech recognition hook
  const { 
    isListening, 
    isSupported: speechRecognitionSupported, 
    startListening, 
    stopListening 
  } = useSpeechRecognition({
    onResult: (transcript) => {
      if (voiceMode && transcript.trim()) {
        onSend(transcript);
      }
    },
  });

  // Speech synthesis hook
  const { 
    speak, 
    stop: stopSpeaking, 
    isSpeaking, 
    isSupported: speechSynthesisSupported 
  } = useSpeechSynthesis({ rate: 1.1 });

  // Auto-speak new AI messages when autoSpeak is enabled
  useEffect(() => {
    if (!autoSpeak || isLoading) return;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.isAI && lastMessage.text && lastMessage.id !== lastSpokenMessageRef.current) {
      // Clean markdown from text for speech
      const cleanText = lastMessage.text
        .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
        .replace(/\*(.*?)\*/g, '$1') // Italic
        .replace(/#{1,6}\s/g, '') // Headers
        .replace(/\|.*?\|/g, '') // Tables
        .replace(/```[\s\S]*?```/g, 'code block') // Code blocks
        .replace(/`(.*?)`/g, '$1') // Inline code
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
        .replace(/[-*+]\s/g, '') // List markers
        .trim();
      
      if (cleanText) {
        speak(cleanText);
        lastSpokenMessageRef.current = lastMessage.id;
      }
    }
  }, [messages, autoSpeak, isLoading, speak]);

  // Toggle voice mode
  const handleVoiceModeToggle = useCallback(() => {
    if (voiceMode) {
      stopListening();
      setVoiceMode(false);
    } else {
      setVoiceMode(true);
    }
  }, [voiceMode, stopListening]);

  // Toggle auto-speak
  const handleAutoSpeakToggle = useCallback(() => {
    if (autoSpeak) {
      stopSpeaking();
    }
    setAutoSpeak(!autoSpeak);
  }, [autoSpeak, stopSpeaking]);

  // Start listening when voice mode is on and AI finishes responding
  useEffect(() => {
    if (voiceMode && !isLoading && !isListening && !isSpeaking && speechRecognitionSupported) {
      const timer = setTimeout(() => {
        startListening();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [voiceMode, isLoading, isListening, isSpeaking, startListening, speechRecognitionSupported]);

  // Auto-scroll to bottom on new messages and initial load
  const prevMessageCount = useRef(0);
  const isInitialLoad = useRef(true);
  
  useEffect(() => {
    if (isInitialLoad.current && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      isInitialLoad.current = false;
      prevMessageCount.current = messages.length;
      return;
    }
    
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevMessageCount.current = messages.length;
  }, [messages]);
  
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isLoading]);

  // Handle scroll to load more
  const handleScroll = useCallback(async () => {
    if (!scrollContainerRef.current || !onLoadMore || !hasMore || loadingMore) return;
    
    const { scrollTop } = scrollContainerRef.current;
    if (scrollTop === 0) {
      setLoadingMore(true);
      await onLoadMore();
      setLoadingMore(false);
    }
  }, [onLoadMore, hasMore, loadingMore]);

  // Handle search
  const handleSearch = useCallback(async () => {
    if (!onSearch || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    const results = await onSearch(searchQuery);
    setSearchResults(results);
    setIsSearching(false);
  }, [onSearch, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, handleSearch]);

  const displayMessages = searchQuery && searchResults.length > 0 ? searchResults : messages;

  // Only show voice controls on desktop where speech recognition is reliable
  const voiceSupported = !isMobile && (speechRecognitionSupported || speechSynthesisSupported);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Voice mode indicator */}
      {voiceMode && (
        <div className={cn(
          "px-4 py-2 border-b border-border/30 flex items-center justify-center gap-2 transition-all",
          isListening ? "bg-destructive/20 animate-pulse" : "bg-primary/10"
        )}>
          <Mic className={cn("w-4 h-4", isListening ? "text-destructive" : "text-primary")} />
          <span className="text-xs font-medium">
            {isListening ? "Listening..." : isSpeaking ? "Speaking..." : "Voice mode active"}
          </span>
          {isSpeaking && <Volume2 className="w-4 h-4 text-primary animate-pulse" />}
        </div>
      )}

      {/* Search bar */}
      {showSearch && (
        <div className="px-4 py-2 bg-secondary/30 border-b border-border/30 flex gap-2 items-center animate-fade-in">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chat history..."
            className="flex-1 h-8 text-sm bg-transparent border-none focus-visible:ring-0"
            autoFocus
          />
          {isSearching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              setShowSearch(false);
              setSearchQuery('');
              setSearchResults([]);
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* AI consent denied message */}
      {consentDeniedMessage && (
        <Alert className="mx-4 mt-2 border-primary/30 bg-primary/5">
          <ShieldAlert className="h-4 w-4 text-primary" />
          <AlertDescription className="flex items-center justify-between gap-2">
            <span className="text-sm">AI features are unavailable without consent. Tap a suggestion to enable.</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={onDismissDeniedMessage}
            >
              <X className="w-4 h-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Messages container - extra bottom padding to account for: 
           - Chat input (~80px) 
           - Optional Go Workout button (~68px) 
           - Bottom nav (~64px)
           - Safe area bottom */}
      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={`flex-1 overflow-y-auto px-3 sm:px-4 pt-4 space-y-3 sm:space-y-4 min-h-0 ${hasWorkoutButton ? 'pb-[17rem]' : 'pb-[13rem]'}`}
      >
        {/* Load more indicator */}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        )}

        {/* Scroll to load more hint */}
        {hasMore && !loadingMore && messages.length > 0 && (
          <div className="flex justify-center py-2">
            <button 
              onClick={onLoadMore}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
              Load earlier messages
            </button>
          </div>
        )}

        {displayMessages.length === 0 && !searchQuery && (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <div className="w-20 h-20 rounded-full evolve-gradient flex items-center justify-center mb-6 pulse-glow">
              <Flame className="w-10 h-10 text-foreground" />
            </div>
            <h2 className="font-display text-xl font-bold mb-2">
              Welcome to <span className="evolve-text-gradient">EVOLVE</span>
            </h2>
            <p className="text-muted-foreground text-sm max-w-xs">
              I'm your personal AI coach — not a one-off plan, but here with you every single day. Let's start.
            </p>
            <div className="mt-6 space-y-2">
              <p className="text-xs text-muted-foreground">Try asking:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {["Help me build muscle", "Create a workout plan", "Track my PRs"].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => onSend(suggestion)}
                    className="px-3 py-1.5 text-xs bg-secondary/50 border border-border/50 rounded-full hover:bg-secondary hover:border-primary/30 transition-all"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {searchQuery && displayMessages.length === 0 && !isSearching && (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <p className="text-muted-foreground text-sm">No messages found for "{searchQuery}"</p>
          </div>
        )}

        {displayMessages
          .filter(message => message.text || !message.isAI)
          .map((message) => (
          <ChatBubble key={message.id} message={message} botIcon={botIcon} />
        ))}

        {isLoading && (
          <div className="flex gap-2 flex-row-reverse animate-fade-in-up">
            <div className="w-8 h-8 rounded-full evolve-gradient flex items-center justify-center shrink-0 evolve-glow">
              <BotIconComponent className="w-4 h-4 text-foreground animate-pulse" />
            </div>
            <div className="chat-bubble-ai px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message counter / Trial banner with voice controls */}
      {isInTrial ? (
        <div className="px-4 py-2 bg-primary/10 border-t border-primary/30 flex justify-between items-center">
          <p className="text-xs text-primary font-medium">
            🔥 Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left — Unlimited messages!
          </p>
          <div className="flex items-center gap-2">
            {voiceSupported && (
              <VoiceModeToggle
                voiceMode={voiceMode}
                onToggle={handleVoiceModeToggle}
                autoSpeak={autoSpeak}
                onAutoSpeakToggle={handleAutoSpeakToggle}
                isListening={isListening}
                isSpeaking={isSpeaking}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : !isPremium ? (
        <div className="px-4 py-2 bg-secondary/30 border-t border-border/30 flex justify-between items-center">
          <p className="text-xs text-muted-foreground">
            <span className={messagesUsed >= messageLimit * 0.8 ? "text-primary font-medium" : ""}>
              {messagesUsed}/{messageLimit}
            </span> messages today
          </p>
          <div className="flex items-center gap-2">
            {voiceSupported && (
              <VoiceModeToggle
                voiceMode={voiceMode}
                onToggle={handleVoiceModeToggle}
                autoSpeak={autoSpeak}
                onAutoSpeakToggle={handleAutoSpeakToggle}
                isListening={isListening}
                isSpeaking={isSpeaking}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2 bg-secondary/30 border-t border-border/30 flex justify-end items-center gap-2">
          {voiceSupported && (
            <VoiceModeToggle
              voiceMode={voiceMode}
              onToggle={handleVoiceModeToggle}
              autoSpeak={autoSpeak}
              onAutoSpeakToggle={handleAutoSpeakToggle}
              isListening={isListening}
              isSpeaking={isSpeaking}
            />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setShowSearch(!showSearch)}
          >
            <Search className="w-3 h-3" />
          </Button>
        </div>
      )}

      <ChatInput 
        onSend={onSend} 
        isLoading={isLoading} 
        disabled={isLimitReached}
        userId={userId}
        hasWorkoutButton={hasWorkoutButton}
        onMicClick={() => {
          console.log('[ChatTab] Mic clicked, isMobile:', isMobile, 'speechRecognitionSupported:', speechRecognitionSupported);
          // On mobile, always suggest system dictation (more reliable than web speech API)
          if (isMobile) {
            import('sonner').then(({ toast }) => {
              toast.info("Tip: Use your keyboard's microphone button for voice input - it's more reliable!", {
                duration: 4000,
              });
            });
            return;
          }
          if (!speechRecognitionSupported) {
            import('sonner').then(({ toast }) => {
              toast.error("Voice input not supported on this browser.");
            });
            return;
          }
          if (isListening) {
            stopListening();
          } else {
            setVoiceMode(true);
            startListening();
          }
        }}
        isListening={isListening}
        showMicButton={!isMobile}
      />
    </div>
  );
}
